// ---------------------------------------------------------------------------
// 3D renderer — Three.js perspective view of the same world state.
// Meshes are synced from the replica each frame; geometry is shared unit
// primitives (scaled per object) and face textures come from the same
// FaceCache the 2D renderer uses.
// ---------------------------------------------------------------------------

import * as THREE from "three";
import { CARD_THICKNESS, getDef, type ChessPieceKind, type FaceSpec, type ObjectDef } from "../shared/defs";
import { clamp } from "../shared/math";
import { supportHeightAt, TABLE } from "../shared/state";
import { facesVersion, getCounterBadges, getCursorTag, getDieLabel, getFace, getHandCursor, getNameTag, TAG_SCALE } from "./faces";
import type { RenderObj, Replica } from "./replica";
import type { Renderer } from "./renderer";
import type { Settings } from "./settings";

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const UNIT_CYL = new THREE.CylinderGeometry(0.5, 0.5, 1, 28);
const UNIT_PLANE = new THREE.PlaneGeometry(1, 1);

// ------------------------------------------------- rounded flat geometries

function roundedRectShape(w: number, d: number, r: number): THREE.Shape {
  const hw = w / 2, hd = d / 2;
  r = Math.min(r, hw, hd);
  const s = new THREE.Shape();
  s.moveTo(-hw + r, -hd);
  s.lineTo(hw - r, -hd);
  s.absarc(hw - r, -hd + r, r, -Math.PI / 2, 0, false);
  s.lineTo(hw, hd - r);
  s.absarc(hw - r, hd - r, r, 0, Math.PI / 2, false);
  s.lineTo(-hw + r, hd);
  s.absarc(-hw + r, hd - r, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-hw, -hd + r);
  s.absarc(-hw + r, -hd + r, r, Math.PI, Math.PI * 1.5, false);
  return s;
}

/** Remap ShapeGeometry's raw-coordinate UVs to [0,1] matching BoxGeometry's top face. */
function remapCapUV(geo: THREE.BufferGeometry, w: number, d: number): void {
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) / w + 0.5, uv.getY(i) / d + 0.5);
  }
  uv.needsUpdate = true;
}

interface CapGeos { top: THREE.BufferGeometry; bottom: THREE.BufferGeometry }
const capGeoCache = new Map<string, CapGeos>();

/** Horizontal caps (top faces +y, bottom faces −y) for a rounded flat object. */
function roundedCaps(w: number, d: number, r: number): CapGeos {
  const key = `${w}|${d}|${r}`;
  let c = capGeoCache.get(key);
  if (!c) {
    const shape = roundedRectShape(w, d, r);
    const top = new THREE.ShapeGeometry(shape, 6);
    remapCapUV(top, w, d);
    top.rotateX(-Math.PI / 2);
    const bottom = new THREE.ShapeGeometry(shape, 6);
    remapCapUV(bottom, w, d);
    bottom.rotateX(Math.PI / 2);
    // A horizontal (z-axis) flip would otherwise show the back 180° rotated.
    bottom.rotateY(Math.PI);
    c = { top, bottom };
    capGeoCache.set(key, c);
  }
  return c;
}

const sideGeoCache = new Map<string, THREE.BufferGeometry>();

/** Rounded-rect side wall of the given height (caps hidden). */
function roundedSide(w: number, d: number, r: number, h: number): THREE.BufferGeometry {
  const key = `${w}|${d}|${r}|${h.toFixed(3)}`;
  let g = sideGeoCache.get(key);
  if (!g) {
    g = new THREE.ExtrudeGeometry(roundedRectShape(w, d, r), {
      depth: h,
      bevelEnabled: false,
      curveSegments: 6,
    });
    g.rotateX(-Math.PI / 2);       // extrusion now spans y ∈ [0, h]
    g.translate(0, -h / 2, 0);
    sideGeoCache.set(key, g);
  }
  return g;
}

const INVISIBLE_MAT = new THREE.MeshBasicMaterial({ visible: false });
INVISIBLE_MAT.userData.shared = true;
const UNIT_SPHERE = new THREE.SphereGeometry(0.5, 18, 14);

// ------------------------------------------------------------ chess pieces
//
// Unit-space geometry (x/z radius as fraction of width, y ∈ [-0.5, 0.5])
// scaled per def, so all six silhouettes share one cached geometry set.

/** Lathe a [radius, height 0..1] profile (chessGeos recenters to unit space). */
function lathe(pts: Array<[number, number]>): THREE.BufferGeometry {
  return new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), 24);
}

/** Arc of profile points around (cx, cy), for balls/domes on the lathe axis. */
function arcPts(cy: number, r: number, a0: number, a1: number, n: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return out;
}

const CHESS_BASE: Array<[number, number]> = [
  [0, 0], [0.44, 0], [0.47, 0.035], [0.42, 0.09], [0.30, 0.14],
];

function chessGeosFor(piece: ChessPieceKind): THREE.BufferGeometry[] {
  switch (piece) {
    case "pawn":
      return [lathe([
        ...CHESS_BASE,
        [0.20, 0.22], [0.16, 0.36], [0.15, 0.50],
        [0.25, 0.55], [0.25, 0.60], [0.16, 0.63],
        ...arcPts(0.79, 0.21, -1.0, Math.PI / 2, 8),
      ])];
    case "rook": {
      const geos = [lathe([
        ...CHESS_BASE,
        [0.23, 0.24], [0.20, 0.55], [0.21, 0.72],
        [0.32, 0.77], [0.32, 0.90], [0, 0.90],
      ])];
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        const tooth = new THREE.BoxGeometry(0.15, 0.10, 0.15);
        tooth.translate(Math.cos(a) * 0.23, 0.94, Math.sin(a) * 0.23);
        geos.push(tooth);
      }
      return geos;
    }
    case "knight": {
      // Lathe collar base + an extruded horse-head silhouette facing +x.
      const geos = [lathe([...CHESS_BASE, [0.22, 0.24], [0.20, 0.30], [0, 0.30]])];
      const s = new THREE.Shape();
      const pts: Array<[number, number]> = [
        [-0.21, 0.26], [0.15, 0.26],          // collar
        [0.20, 0.34], [0.27, 0.44],           // chest, up-forward
        [0.45, 0.54], [0.48, 0.64],           // muzzle underside → nose
        [0.30, 0.70], [0.17, 0.74],           // top of muzzle, brow
        [0.12, 0.92], [0.02, 0.80],           // first ear
        [-0.05, 0.90], [-0.13, 0.77],         // second ear, back of head
        [-0.19, 0.60], [-0.22, 0.40],         // arched back of neck
      ];
      s.moveTo(pts[0]![0], pts[0]![1]);
      for (const [x, y] of pts.slice(1)) s.lineTo(x, y);
      s.closePath();
      const head = new THREE.ExtrudeGeometry(s, {
        depth: 0.34, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.06, bevelSegments: 3,
      });
      head.translate(0, 0, -0.17);
      geos.push(head);
      return geos;
    }
    case "bishop":
      return [lathe([
        ...CHESS_BASE,
        [0.18, 0.24], [0.14, 0.42], [0.13, 0.55],
        [0.21, 0.60], [0.14, 0.64],
        [0.19, 0.72], [0.17, 0.80], [0.10, 0.87], [0.05, 0.905],
        ...arcPts(0.945, 0.05, -1.3, Math.PI / 2, 6),
      ])];
    case "queen":
      return [lathe([
        ...CHESS_BASE,
        [0.22, 0.20], [0.15, 0.40], [0.13, 0.62],
        [0.20, 0.66], [0.14, 0.70],
        [0.27, 0.87], [0.29, 0.93], [0.21, 0.895], [0.10, 0.88],
        ...arcPts(0.935, 0.062, -1.2, Math.PI / 2, 6),
      ])];
    case "king": {
      const geos = [lathe([
        ...CHESS_BASE,
        [0.23, 0.20], [0.16, 0.40], [0.14, 0.62],
        [0.21, 0.66], [0.15, 0.70],
        [0.28, 0.84], [0.28, 0.87], [0, 0.87],
      ])];
      const post = new THREE.BoxGeometry(0.055, 0.13, 0.055);
      post.translate(0, 0.935, 0);
      const bar = new THREE.BoxGeometry(0.15, 0.05, 0.055);
      bar.translate(0, 0.95, 0);
      geos.push(post, bar);
      return geos;
    }
  }
}

const chessGeoCache = new Map<string, THREE.BufferGeometry[]>();
function chessGeos(piece: ChessPieceKind): THREE.BufferGeometry[] {
  let g = chessGeoCache.get(piece);
  if (!g) {
    g = chessGeosFor(piece);
    for (const geo of g) geo.translate(0, -0.5, 0); // 0..1 → centered unit space
    chessGeoCache.set(piece, g);
  }
  return g;
}

const chessMatCache = new Map<string, THREE.MeshLambertMaterial>();
function chessMat(color: string): THREE.MeshLambertMaterial {
  let m = chessMatCache.get(color);
  if (!m) {
    // DoubleSide: the queen's crown cup and the knight head fold the surface.
    m = shared(new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide }));
    chessMatCache.set(color, m);
  }
  return m;
}

/** Mark a material/texture as cache-owned so disposeDeep leaves it alone. */
function shared<T extends THREE.Material | THREE.Texture>(res: T): T {
  res.userData.shared = true;
  return res;
}

/**
 * Free the GPU resources a mesh group owns. Meshes are rebuilt whenever an
 * object's meshKey changes (deck counts, rolls, zone growth…), so without
 * this every rebuild leaks materials + canvas textures for the session's
 * lifetime. Geometries are shared caches unless flagged `owned`; materials
 * and their maps are per-mesh unless flagged `shared`.
 */
function disposeDeep(root: THREE.Object3D): void {
  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    const geo = mesh.geometry as THREE.BufferGeometry | undefined;
    if (geo?.userData.owned) geo.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (!mat) return;
    for (const m of Array.isArray(mat) ? mat : [mat]) {
      if (m.userData.shared) continue;
      const map = (m as THREE.MeshLambertMaterial).map;
      if (map && !map.userData.shared) map.dispose();
      m.dispose();
    }
  });
}

// -------------------------------------------------------------- dice geometry

const dieGeoCache = new Map<number, THREE.BufferGeometry>();

/** Rotate a polyhedron so its first face lies flat on the table. */
function restOnFace(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const a = new THREE.Vector3().fromBufferAttribute(pos, 0);
  const b = new THREE.Vector3().fromBufferAttribute(pos, 1);
  const c = new THREE.Vector3().fromBufferAttribute(pos, 2);
  const n = new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a)).normalize();
  const q = new THREE.Quaternion().setFromUnitVectors(n, new THREE.Vector3(0, -1, 0));
  geo.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(q));
  geo.computeVertexNormals(); // non-indexed → per-face normals (faceted look)
  return geo;
}

/** Pentagonal trapezohedron — the d10. Unit height (apexes at y = ±0.5). */
function d10Geometry(): THREE.BufferGeometry {
  const top = new THREE.Vector3(0, 0.5, 0);
  const bottom = new THREE.Vector3(0, -0.5, 0);
  const upper: THREE.Vector3[] = [];
  const lower: THREE.Vector3[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    upper.push(new THREE.Vector3(Math.cos(a) * 0.5, 0.09, Math.sin(a) * 0.5));
    lower.push(new THREE.Vector3(Math.cos(a + Math.PI / 5) * 0.5, -0.09, Math.sin(a + Math.PI / 5) * 0.5));
  }
  const tris: THREE.Vector3[] = [];
  for (let i = 0; i < 5; i++) {
    const u0 = upper[i]!, u1 = upper[(i + 1) % 5]!;
    const l0 = lower[i]!, l1 = lower[(i + 1) % 5]!;
    tris.push(top, l0, u0, top, u1, l0);       // upper kite (2 tris)
    tris.push(bottom, u1, l0, bottom, l1, u1); // lower kite (2 tris)
  }
  const geo = new THREE.BufferGeometry().setFromPoints(tris);
  geo.computeVertexNormals();
  return geo;
}

/** Unit-ish geometry for a die (scaled by the def's size at mesh build). */
function dieGeometry(sides: number): THREE.BufferGeometry {
  let g = dieGeoCache.get(sides);
  if (!g) {
    switch (sides) {
      case 4: g = restOnFace(new THREE.TetrahedronGeometry(0.7)); break;
      case 8: g = restOnFace(new THREE.OctahedronGeometry(0.68)); break;
      case 10: g = d10Geometry(); break;
      case 12: g = restOnFace(new THREE.DodecahedronGeometry(0.6)); break;
      case 20: g = restOnFace(new THREE.IcosahedronGeometry(0.64)); break;
      default: g = new THREE.BoxGeometry(1, 1, 1); break;
    }
    dieGeoCache.set(sides, g);
  }
  return g;
}

/** Mesh rotation that puts a d6 face (by value) on top. Faces: +x=2 −x=5 +y=1 −y=6 +z=3 −z=4. */
const D6_ROT: Record<number, [number, number, number]> = {
  1: [0, 0, 0],
  2: [0, 0, Math.PI / 2],
  3: [-Math.PI / 2, 0, 0],
  4: [Math.PI / 2, 0, 0],
  5: [0, 0, -Math.PI / 2],
  6: [Math.PI, 0, 0],
};

// ------------------------------------------------------- stack side stripes

/** Tile for deck sides: cardboard with one card-edge line per repeat. */
let deckStripeCanvas: HTMLCanvasElement | null = null;
function getDeckStripes(): HTMLCanvasElement {
  if (!deckStripeCanvas) {
    const c = document.createElement("canvas");
    c.width = 8;
    c.height = 32;
    const g = c.getContext("2d")!;
    g.fillStyle = "#ddd6c6";
    g.fillRect(0, 0, 8, 32);
    g.fillStyle = "rgba(255,255,255,0.55)";
    g.fillRect(0, 2, 8, 8);
    g.fillStyle = "rgba(105,88,66,0.5)";
    g.fillRect(0, 29, 8, 3);
    deckStripeCanvas = c;
  }
  return deckStripeCanvas;
}

/** Tile for chip sides: base color, separation groove + white edge dash. */
const chipStripeCache = new Map<string, HTMLCanvasElement>();
function getChipStripes(color: string): HTMLCanvasElement {
  let c = chipStripeCache.get(color);
  if (!c) {
    c = document.createElement("canvas");
    c.width = 48;
    c.height = 24;
    const g = c.getContext("2d")!;
    g.fillStyle = color;
    g.fillRect(0, 0, 48, 24);
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.fillRect(12, 3, 24, 17);      // edge dash (repeats around the rim)
    g.fillStyle = "rgba(0,0,0,0.35)";
    g.fillRect(0, 21, 48, 3);       // separation groove between chips
    chipStripeCache.set(color, c);
  }
  return c;
}

// Textures for stack sides / die labels are shared across mesh rebuilds
// (repeat settings live on the texture, so per-count variants get their own).
let deckStripeTex: THREE.Texture | null = null;
function getDeckStripeTex(): THREE.Texture {
  if (!deckStripeTex) {
    deckStripeTex = shared(new THREE.CanvasTexture(getDeckStripes()));
    deckStripeTex.colorSpace = THREE.SRGBColorSpace;
    deckStripeTex.wrapS = deckStripeTex.wrapT = THREE.RepeatWrapping;
    // Extrude side UV.v is raw height units → one stripe per card.
    deckStripeTex.repeat.set(1, 1 / CARD_THICKNESS);
  }
  return deckStripeTex;
}

const chipStripeTexCache = new Map<string, THREE.Texture>();
function getChipStripeTex(color: string, count: number): THREE.Texture {
  const key = `${color}|${count}`;
  let t = chipStripeTexCache.get(key);
  if (!t) {
    t = shared(new THREE.CanvasTexture(getChipStripes(color)));
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(12, count); // 12 dashes around the rim, one row per chip
    chipStripeTexCache.set(key, t);
  }
  return t;
}

const dieLabelTexCache = new Map<number, THREE.Texture>();
function getDieLabelTex(value: number): THREE.Texture {
  let t = dieLabelTexCache.get(value);
  if (!t) {
    t = shared(new THREE.CanvasTexture(getDieLabel(value)));
    t.colorSpace = THREE.SRGBColorSpace;
    dieLabelTexCache.set(value, t);
  }
  return t;
}

export class Render3D implements Renderer {
  readonly mode = "3d" as const;

  constructor(private settings?: Settings) {}

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private container: HTMLElement | null = null;
  private raycaster = new THREE.Raycaster();
  private scratchColor = new THREE.Color();
  private meshes = new Map<string, THREE.Group>();
  private pickList: THREE.Group[] = [];
  private pickListDirty = true;
  private cursorMeshes = new Map<string, THREE.Group>();
  private texCache = new Map<string, THREE.Texture>();
  private time = 0;
  private facesSeen = 0;
  private dropGhost: THREE.Line | null = null;

  // Orbit camera state.
  private target = new THREE.Vector3(0, 0, 0);
  private camYaw = 0;       // azimuth: 0 = looking from +z (south seat)
  private camPitch = 0.85;
  private camDist = 135;
  /** Current room's table dims (updated from the replica each frame). */
  private table: { w: number; d: number } = { w: TABLE.w, d: TABLE.d };
  private tableGroup: THREE.Group | null = null;
  private feltMat!: THREE.MeshLambertMaterial;
  private woodMat!: THREE.MeshLambertMaterial;
  private woodLongMat!: THREE.MeshLambertMaterial;

  mount(container: HTMLElement): void {
    this.container = container;
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    const shadows = this.settings?.data.shadowQuality ?? "medium";
    this.renderer.shadowMap.enabled = shadows !== "off";
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.className = "game-canvas";
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x17181c);
    this.scene.fog = new THREE.Fog(0x17181c, 320, 620);
    this.camera = new THREE.PerspectiveCamera(48, 1, 1, 1200);

    this.buildEnvironment();
    this.resize();
  }

  unmount(): void {
    this.renderer.domElement.remove();
    this.renderer.dispose();
    this.meshes.clear();
    this.cursorMeshes.clear();
    this.container = null;
  }

  resize(): void {
    if (!this.container) return;
    const w = this.container.clientWidth, h = this.container.clientHeight;
    this.renderer.setSize(w, h, true);
    // renderScale above the device pixel ratio supersamples (SSAA-like).
    this.renderer.setPixelRatio(this.settings?.data.renderScale ?? Math.min(window.devicePixelRatio || 1, 2));
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // -------------------------------------------------------------- helpers

  private tex(spec: FaceSpec, w: number, h: number): THREE.Texture {
    const key = JSON.stringify(spec) + `|${w}x${h}`;
    let t = this.texCache.get(key);
    if (!t) {
      t = shared(new THREE.CanvasTexture(getFace(spec, w, h)));
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = Math.min(16, this.renderer?.capabilities.getMaxAnisotropy() ?? 4);
      this.texCache.set(key, t);
    }
    return t;
  }

  private buildEnvironment(): void {
    // Lights.
    this.scene.add(new THREE.HemisphereLight(0xcdd4e0, 0x33302a, 1.15));
    const sun = new THREE.DirectionalLight(0xfff1dd, 2.0);
    sun.position.set(70, 150, 50);
    const shadowQ = this.settings?.data.shadowQuality ?? "medium";
    sun.castShadow = shadowQ !== "off";
    const mapSize =
      shadowQ === "ultra" ? 8192 : shadowQ === "high" ? 4096 : shadowQ === "low" ? 1024 : 2048;
    sun.shadow.mapSize.set(mapSize, mapSize);
    const S = 140;
    sun.shadow.camera.left = -S;
    sun.shadow.camera.right = S;
    sun.shadow.camera.top = S;
    sun.shadow.camera.bottom = -S;
    sun.shadow.camera.far = 400;
    // Bias pair tuned for thin lifted cards: normalBias fixes acne on flat
    // geometry without detaching the contact shadow.
    sun.shadow.bias = -0.00015;
    sun.shadow.normalBias = 0.5;
    this.scene.add(sun);

    // Floor far below.
    const floor = new THREE.Mesh(
      UNIT_PLANE,
      new THREE.MeshLambertMaterial({ color: 0x101014 }),
    );
    floor.scale.set(2000, 2000, 1);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -42;
    this.scene.add(floor);

    // Felt table top (surface at y = 0). Procedural gradient immediately,
    // upgraded in place by a real CC0 fabric texture (green-tinted) on load.
    const feltCanvas = document.createElement("canvas");
    feltCanvas.width = feltCanvas.height = 2048;
    const fg = feltCanvas.getContext("2d")!;
    const grad = fg.createRadialGradient(1024, 1024, 160, 1024, 1024, 1440);
    grad.addColorStop(0, "#2e6b45");
    grad.addColorStop(1, "#235034");
    fg.fillStyle = grad;
    fg.fillRect(0, 0, 2048, 2048);
    for (let i = 0; i < 24000; i++) {
      fg.fillStyle = `rgba(0,0,0,${Math.random() * 0.05})`;
      fg.fillRect(Math.random() * 2048, Math.random() * 2048, 2, 2);
    }
    const feltTex = new THREE.CanvasTexture(feltCanvas);
    feltTex.colorSpace = THREE.SRGBColorSpace;
    feltTex.anisotropy = 16;
    // Drop a seamless felt image at public/textures/felt.jpg to replace the
    // procedural felt (used as-is, no tinting — pick one that IS your felt).
    const feltImg = new Image();
    feltImg.onload = () => {
      fg.globalCompositeOperation = "source-over";
      fg.drawImage(feltImg, 0, 0, 2048, 2048);
      feltTex.needsUpdate = true;
    };
    feltImg.src = "/textures/felt.jpg";
    this.feltMat = new THREE.MeshLambertMaterial({ map: feltTex });

    // Wood rim — real wood grain (Poly Haven CC0), flat color until loaded.
    // Long (north/south) planks need the grain rotated 90° so it runs along
    // the plank; the short (east/west) planks use it as-is.
    const wood = new THREE.MeshLambertMaterial({ color: 0x6a5136 });
    const woodLong = new THREE.MeshLambertMaterial({ color: 0x6a5136 });
    const woodTex = new THREE.TextureLoader().load("/textures/wood.jpg", () => {
      woodTex.colorSpace = THREE.SRGBColorSpace;
      woodTex.wrapS = woodTex.wrapT = THREE.RepeatWrapping;
      woodTex.repeat.set(2, 0.5);
      woodTex.anisotropy = 16;
      wood.map = woodTex;
      wood.color.set(0xffffff);
      wood.needsUpdate = true;
      const rot = woodTex.clone();
      rot.center.set(0.5, 0.5);
      rot.rotation = Math.PI / 2;
      rot.needsUpdate = true;
      woodLong.map = rot;
      woodLong.color.set(0xffffff);
      woodLong.needsUpdate = true;
    });
    this.woodMat = wood;
    this.woodLongMat = woodLong;
    this.rebuildTable();
  }

  /** (Re)build the table top, rim and legs for the current room dims. */
  private rebuildTable(): void {
    if (this.tableGroup) this.scene.remove(this.tableGroup);
    const grp = new THREE.Group();
    this.tableGroup = grp;
    const { w: TW, d: TD } = this.table;

    const top = new THREE.Mesh(UNIT_BOX, this.feltMat);
    top.scale.set(TW, 3, TD);
    top.position.y = -1.5;
    top.receiveShadow = true;
    grp.add(top);

    const rimT = 5, rimH = 4.2;
    const mkRim = (w: number, d: number, x: number, z: number, mat: THREE.Material) => {
      const m = new THREE.Mesh(UNIT_BOX, mat);
      m.scale.set(w, rimH, d);
      m.position.set(x, rimH / 2 - 3, z);
      m.castShadow = m.receiveShadow = true;
      grp.add(m);
    };
    mkRim(TW + rimT * 2, rimT, 0, -(TD / 2 + rimT / 2), this.woodLongMat);
    mkRim(TW + rimT * 2, rimT, 0, TD / 2 + rimT / 2, this.woodLongMat);
    mkRim(rimT, TD, -(TW / 2 + rimT / 2), 0, this.woodMat);
    mkRim(rimT, TD, TW / 2 + rimT / 2, 0, this.woodMat);
    for (const [lx, lz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const leg = new THREE.Mesh(UNIT_CYL, this.woodMat);
      leg.scale.set(7, 40, 7);
      leg.position.set(lx! * (TW / 2 - 12), -22, lz! * (TD / 2 - 10));
      grp.add(leg);
    }
    this.scene.add(grp);
  }

  // -------------------------------------------------------------- camera

  private updateCamera(): void {
    const cp = Math.cos(this.camPitch), sp = Math.sin(this.camPitch);
    this.camera.position.set(
      this.target.x + this.camDist * cp * Math.sin(this.camYaw),
      this.target.y + this.camDist * sp,
      this.target.z + this.camDist * cp * Math.cos(this.camYaw),
    );
    this.camera.lookAt(this.target);
  }

  focusSeat(seat: number): void {
    const yawBySeat = [0, Math.PI, -Math.PI / 2, Math.PI / 2, 0, Math.PI];
    this.camYaw = yawBySeat[seat] ?? 0;
    this.camPitch = 0.82;
    this.camDist = 135 * Math.max(1, this.table.w / 170, this.table.d / 110);
    this.target.set(0, 0, 0);
  }

  cameraDrag(kind: "pan" | "orbit", dx: number, dy: number): void {
    if (kind === "orbit") {
      this.camYaw -= dx * 0.005;
      this.camPitch = clamp(this.camPitch + dy * 0.004, 0.25, 1.5);
    } else {
      // Grab-pan along the table plane relative to camera heading.
      // Screen-right on ground = (cos yaw, 0, −sin yaw); screen-up = (−sin, 0, −cos).
      const speed = this.camDist * 0.0011;
      const sin = Math.sin(this.camYaw), cos = Math.cos(this.camYaw);
      this.target.x -= (dx * cos + dy * sin) * speed;
      this.target.z -= (-dx * sin + dy * cos) * speed;
      this.target.x = clamp(this.target.x, -this.table.w / 2, this.table.w / 2);
      this.target.z = clamp(this.target.z, -this.table.d / 2, this.table.d / 2);
    }
  }

  cameraZoom(delta: number, _px: number, _py: number): void {
    this.camDist = clamp(this.camDist * Math.exp(delta * 0.0011), 9, 320);
  }

  panKeys(dirX: number, dirY: number, dt: number): void {
    // Move the focus point camera-relative: +Y = up-screen = camera-forward
    // projected on the table.
    const k = this.camDist * 0.85 * dt;
    const sin = Math.sin(this.camYaw), cos = Math.cos(this.camYaw);
    this.target.x += (dirX * cos - dirY * sin) * k;
    this.target.z += (-dirX * sin - dirY * cos) * k;
    this.target.x = clamp(this.target.x, -this.table.w / 2, this.table.w / 2);
    this.target.z = clamp(this.target.z, -this.table.d / 2, this.table.d / 2);
  }

  projectToScreen(x: number, z: number, y = 0): { x: number; y: number } | null {
    const v = new THREE.Vector3(x, y, z).project(this.camera);
    if (v.z > 1) return null; // behind the camera
    const r = this.renderer.domElement.getBoundingClientRect();
    return {
      x: r.left + ((v.x + 1) / 2) * r.width,
      y: r.top + ((1 - v.y) / 2) * r.height,
    };
  }

  // ------------------------------------------------------------- picking

  private ndc(px: number, py: number): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((px - r.left) / r.width) * 2 - 1,
      -(((py - r.top) / r.height) * 2 - 1),
    );
  }

  pick(px: number, py: number, _state: Replica): string | null {
    this.raycaster.setFromCamera(this.ndc(px, py), this.camera);
    // pick() runs on every pointermove — keep the candidate list between
    // calls instead of re-filtering the whole mesh map each time.
    if (this.pickListDirty) {
      this.pickListDirty = false;
      this.pickList.length = 0;
      for (const gr of this.meshes.values()) {
        if (!gr.userData.isZone) this.pickList.push(gr);
      }
    }
    const hits = this.raycaster.intersectObjects(this.pickList, true);
    for (const hit of hits) {
      let obj: THREE.Object3D | null = hit.object;
      while (obj && !obj.userData.objId) obj = obj.parent;
      if (obj?.userData.objId) return obj.userData.objId as string;
    }
    return null;
  }

  screenToTable(px: number, py: number): { x: number; z: number } | null {
    this.raycaster.setFromCamera(this.ndc(px, py), this.camera);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const pt = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(plane, pt)) return null;
    return { x: pt.x, z: pt.z };
  }

  // ------------------------------------------------------------ mesh sync

  /** Build (or rebuild) the visual for an object. Returns a fresh group. */
  private buildMesh(o: RenderObj, d: ObjectDef): THREE.Group {
    const group = new THREE.Group();
    group.userData.objId = o.id;
    group.userData.isZone = d.kind === "zone";
    const [w, h, dp] = d.size;

    const side = new THREE.MeshLambertMaterial({ color: d.color ?? "#f2efe4" });

    switch (d.kind) {
      case "card": {
        const r = d.cornerRadius ?? 0;
        const inner = new THREE.Group(); // flip animation rotates this
        const t = CARD_THICKNESS * 2;    // slightly thick so it reads
        if (r > 0) {
          const caps = roundedCaps(w, dp, r);
          const topMat = new THREE.MeshLambertMaterial({ map: d.front ? this.tex(d.front, w, dp) : null });
          const botMat = new THREE.MeshLambertMaterial({ map: d.back ? this.tex(d.back, w, dp) : null });
          // Single-sided planes must cast from either side or shadows vanish
          // mid-flip / for the face pointing away from the sun.
          topMat.shadowSide = THREE.DoubleSide;
          botMat.shadowSide = THREE.DoubleSide;
          const top = new THREE.Mesh(caps.top, topMat);
          top.position.y = t / 2;
          const bottom = new THREE.Mesh(caps.bottom, botMat);
          bottom.position.y = -t / 2;
          top.castShadow = bottom.castShadow = true;
          top.receiveShadow = bottom.receiveShadow = true;
          inner.add(top, bottom);
        } else {
          const mats = [
            side, side,
            new THREE.MeshLambertMaterial({ map: d.front ? this.tex(d.front, w, dp) : null }),
            new THREE.MeshLambertMaterial({ map: d.back ? this.tex(d.back, w, dp) : null }),
            side, side,
          ];
          const m = new THREE.Mesh(UNIT_BOX, mats);
          m.scale.set(w, t, dp);
          m.castShadow = m.receiveShadow = true;
          inner.add(m);
        }
        group.add(inner);
        break;
      }
      case "stack": {
        const count = Math.max(1, o.count ?? 1);
        if (d.acceptsGroup?.startsWith("cards")) {
          // Deck: rounded side wall with one edge-line per card + textured caps.
          const h = count * CARD_THICKNESS;
          const r = d.cornerRadius ?? 0;
          const topSpec: FaceSpec | undefined =
            o.faceUp && o.topDef ? getDef(o.topDef).front ?? d.back : d.back;
          const botSpec = d.back;

          const sideMat = new THREE.MeshLambertMaterial({ map: getDeckStripeTex() });

          sideMat.shadowSide = THREE.DoubleSide;
          const wall = new THREE.Mesh(roundedSide(w, dp, r, h), [INVISIBLE_MAT, sideMat]);
          wall.castShadow = wall.receiveShadow = true;
          const caps = roundedCaps(w, dp, r);
          const topMat = new THREE.MeshLambertMaterial({ map: topSpec ? this.tex(topSpec, w, dp) : null });
          const botMat = new THREE.MeshLambertMaterial({ map: botSpec ? this.tex(botSpec, w, dp) : null });
          // Fill the deck's shadow footprint: without double-sided casting the
          // wall casts a hollow ring and the caps get culled by the light.
          topMat.shadowSide = THREE.DoubleSide;
          botMat.shadowSide = THREE.DoubleSide;
          const top = new THREE.Mesh(caps.top, topMat);
          top.position.y = h / 2 + 0.002;
          top.castShadow = true;
          const bottom = new THREE.Mesh(caps.bottom, botMat);
          bottom.position.y = -h / 2 - 0.002;
          bottom.castShadow = true;
          group.add(wall, top, bottom);
        } else {
          this.buildCylinder(group, o, d, count);
        }
        break;
      }
      case "token": {
        if (d.piece) {
          const mat = chessMat(d.color ?? "#cccccc");
          for (const geo of chessGeos(d.piece)) {
            const m = new THREE.Mesh(geo, mat);
            m.scale.set(w, h, dp);
            m.castShadow = m.receiveShadow = true;
            group.add(m);
          }
        } else if (d.shape === "cylinder" && d.front) {
          this.buildCylinder(group, o, d, 1);
        } else if (d.shape === "cylinder") {
          // Pawn: body + head.
          const mat = new THREE.MeshLambertMaterial({ color: d.color ?? "#999" });
          const body = new THREE.Mesh(UNIT_CYL, mat);
          body.scale.set(w, h * 0.72, dp);
          body.position.y = -h * 0.14;
          body.castShadow = true;
          const head = new THREE.Mesh(UNIT_SPHERE, mat);
          head.scale.set(w * 0.85, w * 0.85, w * 0.85);
          head.position.y = h * 0.3;
          head.castShadow = true;
          group.add(body, head);
        } else {
          const m = new THREE.Mesh(UNIT_BOX, side);
          m.scale.set(w, h, dp);
          m.castShadow = m.receiveShadow = true;
          group.add(m);
        }
        break;
      }
      case "board": {
        if (d.flat) {
          // Printed mat: a rounded-corner decal lying on the felt — no slab.
          // transparent:true keeps the art's antialiased corner pixels clean.
          const mat = new THREE.MeshLambertMaterial({
            map: d.front ? this.tex(d.front, w, dp) : null,
            transparent: true,
          });
          const m = new THREE.Mesh(roundedCaps(w, dp, d.cornerRadius ?? 0).top, mat);
          m.receiveShadow = true;
          group.add(m);
          break;
        }
        const mats = [
          side, side,
          new THREE.MeshLambertMaterial({ map: d.front ? this.tex(d.front, w, dp) : null }),
          side, side, side,
        ];
        const m = new THREE.Mesh(UNIT_BOX, mats);
        m.scale.set(w, h, dp);
        m.castShadow = m.receiveShadow = true;
        group.add(m);
        break;
      }
      case "zone": {
        // Zones live BELOW every resting object (cards sit at y≈0.03) and
        // never write depth, so they can't draw over or z-fight the cards.
        // Width grows with the cards inside (o.zw); rounded corners.
        const zwNow = o.zw ?? w;
        const shape = roundedRectShape(zwNow, dp, 2.6);
        const planeGeo = new THREE.ShapeGeometry(shape, 8);
        planeGeo.userData.owned = true;
        planeGeo.rotateX(-Math.PI / 2);
        const plane = new THREE.Mesh(
          planeGeo,
          new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.14, color: 0xffffff, depthWrite: false }),
        );
        plane.position.y = 0.012;
        plane.renderOrder = -2;
        group.add(plane);
        const outline = shape.getPoints(24).map((v) => new THREE.Vector3(v.x, 0.016, -v.y));
        outline.push(outline[0]!.clone());
        const outlineGeo = new THREE.BufferGeometry().setFromPoints(outline);
        outlineGeo.userData.owned = true;
        const line = new THREE.Line(
          outlineGeo,
          new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4, depthWrite: false }),
        );
        line.renderOrder = -1;
        group.add(line);
        break;
      }
      case "die": {
        const sides = d.sides ?? 6;
        const inner = new THREE.Group(); // tumble fx wobbles this
        inner.name = "die-inner";
        if (sides === 6) {
          // Real pip (or labeled) faces; the mesh is oriented so the rolled
          // value is up.
          const mats = [2, 5, 1, 6, 3, 4].map((v) => {
            const spec: FaceSpec = { type: "proc-die", sides: 6, value: v, color: d.color ?? "#efe9da", label: d.labels?.[v - 1] };
            return new THREE.MeshLambertMaterial({ map: this.tex(spec, w, dp) });
          });
          const mesh = new THREE.Mesh(UNIT_BOX, mats);
          mesh.scale.set(w, h, dp);
          mesh.castShadow = mesh.receiveShadow = true;
          const [rx, ry, rz] = D6_ROT[o.value ?? 6] ?? D6_ROT[1]!;
          mesh.rotation.set(rx, ry, rz);
          inner.add(mesh);
        } else if (sides === 2) {
          // The coin: flat cylinder, rolled face up, the other underneath.
          const color = d.color ?? "#d1a333";
          const face = (v: number) =>
            new THREE.MeshLambertMaterial({ map: this.tex({ type: "proc-die", sides: 2, value: v, color }, w, dp) });
          const side = new THREE.MeshLambertMaterial({ color });
          const v = o.value ?? 1;
          const mesh = new THREE.Mesh(UNIT_CYL, [side, face(v), face(v === 1 ? 2 : 1)]);
          mesh.scale.set(w, h, dp);
          mesh.castShadow = mesh.receiveShadow = true;
          inner.add(mesh);
        } else {
          const mat = new THREE.MeshLambertMaterial({ color: d.color ?? "#888", side: THREE.DoubleSide });
          const mesh = new THREE.Mesh(dieGeometry(sides), mat);
          mesh.scale.set(w, h, dp);
          mesh.castShadow = mesh.receiveShadow = true;
          inner.add(mesh);
          // Rolled value rides above the die as a small billboard.
          const sprite = new THREE.Sprite(
            new THREE.SpriteMaterial({ map: getDieLabelTex(o.value ?? sides), depthTest: false }),
          );
          sprite.name = "die-label";
          sprite.scale.set(2.6, 2.6, 1);
          sprite.position.y = h * 0.62;
          sprite.renderOrder = 8;
          inner.add(sprite);
        }
        group.add(inner);
        break;
      }
    }
    return group;
  }

  /** Chip / chip-stack cylinder; side shows one groove + edge dash per chip. */
  private buildCylinder(group: THREE.Group, o: RenderObj, d: ObjectDef, count: number): void {
    const [w, unitH, dp] = d.size;
    const h = unitH * count;
    let sideMat: THREE.MeshLambertMaterial;
    if (d.color && d.front) {
      sideMat = new THREE.MeshLambertMaterial({ map: getChipStripeTex(d.color, count) });
    } else {
      sideMat = new THREE.MeshLambertMaterial({ color: d.color ?? "#888" });
    }
    const faceMat = d.front
      ? new THREE.MeshLambertMaterial({ map: this.tex(d.front, w, dp) })
      : sideMat;
    const m = new THREE.Mesh(UNIT_CYL, [sideMat, faceMat, faceMat]);
    m.scale.set(w, h, dp);
    m.castShadow = m.receiveShadow = true;
    group.add(m);
  }

  /** Key that captures anything requiring a mesh rebuild. */
  private meshKey(o: RenderObj): string {
    return `${o.def}|${o.faceUp ? 1 : 0}|${o.topDef ?? ""}|${o.count ?? 0}|${o.zw ?? 0}|${o.value ?? 0}|${o.counters ? JSON.stringify(o.counters) : ""}`;
  }

  private readonly seenScratch = new Set<string>();

  private syncObjects(state: Replica): void {
    const seen = this.seenScratch;
    seen.clear();
    for (const o of state.objs.values()) {
      seen.add(o.id);
      const d = getDef(o.def);
      let group = this.meshes.get(o.id);
      if (group && group.userData.key !== this.meshKey(o)) {
        this.scene.remove(group);
        disposeDeep(group);
        group = undefined;
        this.pickListDirty = true;
      }
      if (!group) {
        group = this.buildMesh(o, d);
        group.userData.key = this.meshKey(o);
        this.meshes.set(o.id, group);
        this.scene.add(group);
        this.pickListDirty = true;
      }

      // Transform.
      group.position.set(o.rp.x, o.rp.y, o.rp.z);
      group.rotation.y = -o.ryaw;

      if (d.kind === "card") {
        // Flip animation: horizontal flip (about the depth axis) with an arc.
        const mesh = group.children[0]!;
        mesh.rotation.z = (1 - o.flipT) * Math.PI;
        const mid = 1 - Math.abs(o.flipT * 2 - 1);
        group.position.y = o.rp.y + mid * 3;
      }
      // MTG counter badges: billboard at the card's top-right.
      if (o.counters?.length && (d.kind === "card" || d.kind === "stack") && !group.getObjectByName("counters")) {
        const badge = getCounterBadges(o.counters);
        const tex = new THREE.CanvasTexture(badge);
        tex.colorSpace = THREE.SRGBColorSpace;
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false }));
        const bw = 3.2;
        sprite.scale.set(bw, bw * (badge.height / badge.width), 1);
        sprite.name = "counters";
        sprite.position.set(d.size[0] / 2 - 0.6, 1.4, -d.size[2] / 2 + 0.4);
        sprite.renderOrder = 8;
        group.add(sprite);
      }

      // Dice tumble: wobble + bounce that decays to the resting pose.
      if (d.kind === "die") {
        const inner = group.getObjectByName("die-inner");
        const label = group.getObjectByName("die-label");
        if (label) label.visible = o.fxRoll <= 0;
        if (inner) {
          const t = o.fxRoll;
          const hash = o.id.charCodeAt(o.id.length - 1);
          inner.rotation.x = Math.sin(this.time * 29 + hash) * t * 2.4;
          inner.rotation.z = Math.cos(this.time * 23 + hash) * t * 2.4;
          group.position.y = o.rp.y + Math.abs(Math.sin(this.time * 17 + hash)) * t * 5;
        }
      }
      // Shuffle jiggle.
      if (o.fxShuffle > 0) {
        group.rotation.y += Math.sin(this.time * 55) * o.fxShuffle * 0.15;
        group.position.y += Math.abs(Math.sin(this.time * 30)) * o.fxShuffle * 1.2;
      }
      // Spawn pop.
      const pop = 0.8 + 0.2 * (1 - (1 - o.spawnT) ** 2);
      group.scale.setScalar(pop);
    }
    for (const [id, group] of this.meshes) {
      if (!seen.has(id)) {
        this.scene.remove(group);
        disposeDeep(group);
        this.meshes.delete(id);
        this.pickListDirty = true;
      }
    }
  }

  private syncZoneTint(state: Replica): void {
    for (const o of state.objs.values()) {
      const d = getDef(o.def);
      if (d.kind !== "zone") continue;
      const group = this.meshes.get(o.id);
      if (!group) continue;
      const plane = group.children[0] as THREE.Mesh;
      const mat = plane.material as THREE.MeshBasicMaterial;
      if (o.def === "zone.slot") {
        mat.opacity = 0.05;
        continue;
      }
      const owner = state.players.find((p) => p.seat === o.seat && p.connected);
      // color.set() parses a hex string — skip it unless ownership changed.
      const tintKey = owner ? `${owner.color}|${o.seat === state.seat}` : "";
      if (group.userData.tintKey !== tintKey) {
        group.userData.tintKey = tintKey;
        mat.color.set(owner ? owner.color : "#ffffff");
        mat.opacity = owner ? (o.seat === state.seat ? 0.22 : 0.12) : 0.05;
      }
      // Name tag sprite.
      const wantTag = owner ? `${owner.name}|${owner.color}` : "";
      if (group.userData.tagKey !== wantTag) {
        group.userData.tagKey = wantTag;
        const old = group.getObjectByName("tag");
        if (old) {
          group.remove(old);
          disposeDeep(old);
        }
        if (owner) {
          const canvas = getNameTag(owner.name, owner.color);
          const tex = new THREE.CanvasTexture(canvas);
          tex.colorSpace = THREE.SRGBColorSpace;
          const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false }));
          sprite.name = "tag";
          sprite.scale.set(canvas.width / (11 * TAG_SCALE), canvas.height / (11 * TAG_SCALE), 1);
          // Just past the zone's outer edge — off the table, behind the mat.
          sprite.position.set(0, 1.4, o.seat !== undefined ? getDef(o.def).size[2] / 2 + 5.5 : 0);
          group.add(sprite);
        }
      }
    }
  }

  private syncCursors(state: Replica): void {
    const seen = new Set<string>();
    for (const [pid, c] of state.cursors) {
      const p = state.players.find((q) => q.id === pid);
      if (!p) continue;
      seen.add(pid);
      let g = this.cursorMeshes.get(pid);
      if (!g) {
        g = new THREE.Group();
        // Little hand, billboarded above the table.
        const handCanvas = getHandCursor(p.color);
        const handTex = new THREE.CanvasTexture(handCanvas);
        handTex.colorSpace = THREE.SRGBColorSpace;
        const hand = new THREE.Sprite(
          new THREE.SpriteMaterial({ map: handTex, depthTest: false }),
        );
        hand.scale.set(1.9, 1.9, 1);
        hand.position.y = 1.0;
        g.add(hand);
        const canvas = getCursorTag(p.name);
        const tex = new THREE.CanvasTexture(canvas);
        tex.colorSpace = THREE.SRGBColorSpace;
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, opacity: 0.9, transparent: true }));
        sprite.scale.set(canvas.width / (16 * TAG_SCALE), canvas.height / (16 * TAG_SCALE), 1);
        sprite.position.y = 2.6;
        g.add(sprite);
        this.cursorMeshes.set(pid, g);
        this.scene.add(g);
      }
      // Ride above whatever the cursor is over (deck tops, boards…).
      g.position.set(c.rx, supportHeightAt(state.objs.values(), c.rx, c.rz), c.rz);
    }
    for (const [pid, g] of this.cursorMeshes) {
      if (!seen.has(pid)) {
        this.scene.remove(g);
        disposeDeep(g);
        this.cursorMeshes.delete(pid);
      }
    }
  }

  /** Blue ring hugging the object's footprint edge when selected. */
  private syncSelectionOutline(group: THREE.Group, o: RenderObj, selected: boolean): void {
    // Fast path for the common case (nothing selected): getObjectByName walks
    // children, so don't call it every frame for every unselected mesh.
    if (!selected && !group.userData.hasRing) return;
    let ring = group.getObjectByName("sel-outline") as THREE.Mesh | undefined;
    if (!selected) {
      if (ring) {
        group.remove(ring);
        disposeDeep(ring);
      }
      group.userData.hasRing = false;
      return;
    }
    group.userData.hasRing = true;
    const d = getDef(o.def);
    const per = d.acceptsGroup?.startsWith("cards") ? CARD_THICKNESS : d.size[1];
    // Group origin = object CENTER. Flat things ring just above their face;
    // stacks ring flat on the table around their base.
    const topY = o.count
      ? -(o.count * per) / 2 + 0.08
      : d.size[1] / 2 + 0.12;
    const key = `${d.size[0]}|${d.size[2]}|${d.shape ?? "box"}|${d.cornerRadius ?? 0}`;
    if (ring && ring.userData.key === key) {
      ring.position.y = topY;
      return;
    }
    if (ring) {
      group.remove(ring);
      disposeDeep(ring);
    }
    const T = 0.22; // ring thickness (world units)
    const GAP = 0.2; // stand-off from the object edge
    let geo: THREE.BufferGeometry;
    if (d.shape === "cylinder") {
      geo = new THREE.RingGeometry(d.size[0] / 2 + GAP, d.size[0] / 2 + GAP + T, 48);
      geo.rotateX(-Math.PI / 2);
    } else {
      const r = d.cornerRadius ?? 0.4;
      const outer = roundedRectShape(d.size[0] + 2 * (GAP + T), d.size[2] + 2 * (GAP + T), r + GAP + T);
      const inner = roundedRectShape(d.size[0] + 2 * GAP, d.size[2] + 2 * GAP, r + GAP);
      const hole = new THREE.Path();
      hole.curves = inner.curves;
      outer.holes.push(hole);
      geo = new THREE.ShapeGeometry(outer, 12);
      geo.rotateX(-Math.PI / 2);
    }
    geo.userData.owned = true;
    ring = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        color: 0x4da3ff,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
        side: THREE.DoubleSide,
      }),
    );
    ring.name = "sel-outline";
    ring.userData.key = key;
    ring.renderOrder = 10;
    ring.position.y = topY;
    group.add(ring);
  }

  /** Ghost outline at the predicted drop spot. The line mesh is rebuilt only
   *  when the footprint changes; per frame it just moves. */
  private syncDropPreview(state: Replica): void {
    const gGhost = state.dropGhost;
    if (!gGhost) {
      if (this.dropGhost) {
        this.scene.remove(this.dropGhost);
        disposeDeep(this.dropGhost);
        this.dropGhost = null;
      }
      return;
    }
    const key = `${gGhost.w}|${gGhost.d}|${gGhost.cyl ? 1 : 0}`;
    if (!this.dropGhost || this.dropGhost.userData.key !== key) {
      if (this.dropGhost) {
        this.scene.remove(this.dropGhost);
        disposeDeep(this.dropGhost);
      }
      let pts: THREE.Vector3[];
      if (gGhost.cyl) {
        pts = [];
        const R = gGhost.w / 2 + 0.4;
        for (let i = 0; i <= 40; i++) {
          const a = (i / 40) * Math.PI * 2;
          pts.push(new THREE.Vector3(Math.cos(a) * R, 0, Math.sin(a) * R));
        }
      } else {
        const shape = roundedRectShape(gGhost.w + 0.8, gGhost.d + 0.8, 0.8);
        pts = shape.getPoints(20).map((v) => new THREE.Vector3(v.x, 0, -v.y));
        pts.push(pts[0]!.clone());
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      geo.userData.owned = true;
      const line = new THREE.Line(
        geo,
        new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55, depthTest: false }),
      );
      line.userData.key = key;
      line.renderOrder = 9;
      this.scene.add(line);
      this.dropGhost = line;
    }
    this.dropGhost.position.set(
      gGhost.x,
      supportHeightAt(state.objs.values(), gGhost.x, gGhost.z) + 0.15,
      gGhost.z,
    );
    this.dropGhost.rotation.y = -gGhost.yaw;
  }

  // ------------------------------------------------------------ rendering

  render(state: Replica, dt: number, hoveredId: string | null): void {
    this.time += dt;
    // Adopt the room's table size; rebuild the table + refit when it changes.
    if (state.table.w !== this.table.w || state.table.d !== this.table.d) {
      this.table = { w: state.table.w, d: state.table.d };
      this.rebuildTable();
      this.focusSeat(state.seat);
    }
    if (facesVersion() !== this.facesSeen) {
      this.facesSeen = facesVersion();
      for (const t of this.texCache.values()) t.needsUpdate = true;
    }
    this.syncObjects(state);
    this.syncZoneTint(state);
    this.syncCursors(state);

    // Hover / held-by-other / merge-target emissive highlight. Traversing
    // every mesh's materials each frame dominated big rooms, so the resolved
    // glow is cached per group and only written when it changes (the pulsing
    // merge target changes every frame — but that's one object).
    for (const [id, group] of this.meshes) {
      const o = state.objs.get(id);
      if (!o) continue;
      this.syncSelectionOutline(group, o, state.selected.has(id));
      let glow: number;
      if (id === state.dropTargetId) {
        const pulse = 0.25 + 0.15 * Math.sin(this.time * 7);
        glow = this.scratchColor.setRGB(pulse * 0.3, pulse * 0.6, pulse).getHex();
      } else if (id === hoveredId && !o.locked) {
        // Locked scenery (boards, mats) shouldn't flash under the pointer.
        glow = 0x333322;
      } else {
        const holder = o.heldBy && o.heldBy !== state.you
          ? state.players.find((p) => p.id === o.heldBy)
          : null;
        glow = holder ? this.scratchColor.set(holder.color).multiplyScalar(0.35).getHex() : 0;
      }
      if (group.userData.glow !== glow) {
        group.userData.glow = glow;
        group.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            for (const m of mats) {
              if (m instanceof THREE.MeshLambertMaterial) m.emissive.setHex(glow);
            }
          }
        });
      }
    }

    this.syncDropPreview(state);
    this.updateCamera();
    this.renderer.render(this.scene, this.camera);
  }
}
